import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import "./colorPicker.css";

const HEX6 = /^#([0-9a-fA-F]{6})$/;
const HEX3 = /^#([0-9a-fA-F]{3})$/;

function rgbToHex(r, g, b) {
  return (
    "#" +
    [r, g, b]
      .map((n) =>
        Math.max(0, Math.min(255, Math.round(Number(n))))
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
  );
}

function hexToRgb(hex) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return [0, 0, 0];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

function rgbToHsv(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

function hsvToRgb(h, s, v) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

export function toHexColor(value) {
  const raw = String(value || "").trim();
  if (!raw) return "#000000";
  const lower = raw.toLowerCase();
  if (lower === "transparent") return "transparent";
  if (lower === "black") return "#000000";
  if (lower === "white") return "#ffffff";
  if (HEX6.test(raw)) return `#${raw.slice(1).toLowerCase()}`;
  if (HEX3.test(raw)) {
    const h = raw.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  const rgb = raw.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgb) return rgbToHex(rgb[1], rgb[2], rgb[3]);
  const hsl = raw.match(
    /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%/i,
  );
  if (hsl) {
    return rgbToHex(
      ...hslToRgb(Number(hsl[1]), Number(hsl[2]) / 100, Number(hsl[3]) / 100),
    );
  }
  return "#000000";
}

const isTransparentValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase() === "transparent";

export default function ColorPicker({
  value,
  onChange: _onChange,
  onBlur,
  id,
  allowTransparent = false,
  showHex = false,
}) {
  const generatedId = useId().replace(/:/g, "");
  const colorInputId = id || `color-picker-${generatedId}`;
  const rootRef = useRef(null);
  const hexInputRef = useRef(null);
  const onBlurRef = useRef(onBlur);
  const hsvRef = useRef({ h: 0, s: 0, v: 0 });
  const lastHexRef = useRef("");
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [draft, setDraft] = useState("");
  const [hsv, setHsv] = useState({ h: 0, s: 0, v: 0 });

  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  const notifyBlur = useCallback(() => onBlurRef.current?.(), []);

  const transparent = allowTransparent && isTransparentValue(value);
  const hexValue = transparent ? "transparent" : toHexColor(value);
  const swatchValue = transparent ? "#000000" : hexValue;

  useEffect(() => {
    setDraft(transparent ? "transparent" : hexValue);
  }, [hexValue, transparent]);

  useEffect(() => {
    if (transparent) return;
    if (hexValue === lastHexRef.current) return;
    lastHexRef.current = hexValue;
    const next = rgbToHsv(...hexToRgb(hexValue));
    setHsv((prev) => {
      const synced = next.s < 0.015 ? { ...next, h: prev.h } : next;
      hsvRef.current = synced;
      return synced;
    });
  }, [hexValue, transparent]);

  hsvRef.current = hsv;

  useEffect(() => {
    if (!open) return undefined;
    const closePicker = () => {
      setOpen(false);
      notifyBlur();
    };
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closePicker();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closePicker();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    const focusTimer = window.setTimeout(() => {
      hexInputRef.current?.focus();
      hexInputRef.current?.select();
    }, 0);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [notifyBlur, open]);

  const emitHex = (next) => {
    _onChange(next);
  };

  const commitDraft = (raw) => {
    const next = String(raw || "").trim();
    if (allowTransparent && next.toLowerCase() === "transparent") {
      emitHex("transparent");
      setDraft("transparent");
      return;
    }
    const hex = toHexColor(next);
    setDraft(hex);
    emitHex(hex);
  };

  const setFromHsv = (nextHsv) => {
    const clamped = {
      h: Math.max(0, Math.min(360, nextHsv.h)),
      s: Math.max(0, Math.min(1, nextHsv.s)),
      v: Math.max(0, Math.min(1, nextHsv.v)),
    };
    hsvRef.current = clamped;
    setHsv(clamped);
    const hex = rgbToHex(...hsvToRgb(clamped.h, clamped.s, clamped.v));
    lastHexRef.current = hex;
    setDraft(hex);
    emitHex(hex);
  };

  const onSvPointer = (event) => {
    const board = event.currentTarget;
    const move = (clientX, clientY) => {
      const rect = board.getBoundingClientRect();
      const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const v = Math.max(
        0,
        Math.min(1, 1 - (clientY - rect.top) / rect.height),
      );
      setFromHsv({ ...hsvRef.current, s, v });
    };
    event.preventDefault();
    move(event.clientX, event.clientY);
    const onMove = (ev) => move(ev.clientX, ev.clientY);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onHuePointer = (event) => {
    const bar = event.currentTarget;
    const move = (clientX) => {
      const rect = bar.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const h = Math.max(0, Math.min(360, ((clientX - rect.left) / width) * 360));
      setFromHsv({ ...hsvRef.current, h });
    };
    event.preventDefault();
    move(event.clientX);
    const onMove = (ev) => move(ev.clientX);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      className={"colorPicker" + (showHex ? " is-hex-visible" : "")}
      ref={rootRef}
    >
      <button
        type="button"
        id={colorInputId}
        className={
          "colorPickerLabel" + (transparent ? " is-transparent" : "")
        }
        style={transparent ? undefined : { background: swatchValue }}
        title={transparent ? "Transparent" : hexValue}
        aria-label="Open color picker"
        aria-expanded={open}
        onClick={() => {
          const rect = rootRef.current?.getBoundingClientRect();
          setOpenUp(Boolean(rect && rect.bottom > window.innerHeight - 240));
          setOpen((prev) => {
            if (prev) notifyBlur();
            return !prev;
          });
        }}
      />

      <input
        type="text"
        className="colorPickerInputsText drop-shadow-2xl"
        value={draft}
        spellCheck={false}
        aria-label="Color hex value"
        onChange={(evt) => {
          const next = evt.target.value;
          setDraft(next);
          if (
            allowTransparent &&
            next.trim().toLowerCase() === "transparent"
          ) {
            emitHex("transparent");
            return;
          }
          if (HEX6.test(next.trim()) || HEX3.test(next.trim())) {
            emitHex(toHexColor(next.trim()));
          }
        }}
        onBlur={() => {
          commitDraft(draft);
          notifyBlur();
        }}
      />

      {open ? (
        <div
          className={"colorPickerPopover" + (openUp ? " is-up" : "")}
          role="dialog"
          aria-label="Hex color"
        >
          <div
            className="colorPickerSv"
            style={{ backgroundColor: `hsl(${hsv.h}, 100%, 50%)` }}
            onPointerDown={onSvPointer}
          >
            <span
              className="colorPickerSvThumb"
              style={{
                left: `${hsv.s * 100}%`,
                top: `${(1 - hsv.v) * 100}%`,
              }}
            />
          </div>
          <div
            className="colorPickerHue"
            role="slider"
            aria-label="Hue"
            aria-valuemin={0}
            aria-valuemax={360}
            aria-valuenow={Math.round(hsv.h)}
            tabIndex={0}
            onPointerDown={onHuePointer}
          >
            <span
              className="colorPickerHueThumb"
              style={{ left: `${(hsv.h / 360) * 100}%` }}
            />
          </div>
          <label className="colorPickerHexRow">
            <span>HEX</span>
            <input
              ref={hexInputRef}
              type="text"
              spellCheck={false}
              value={draft}
              onChange={(evt) => {
                const next = evt.target.value;
                setDraft(next);
                if (HEX6.test(next.trim()) || HEX3.test(next.trim())) {
                  emitHex(toHexColor(next.trim()));
                }
              }}
              onBlur={() => commitDraft(draft)}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}
